import React, { useState, useEffect } from 'react';
import Modal from 'react-responsive-modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Lock, AlertCircle } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { useValidatePinMutation } from 'services/kewloxApi';
import { PIN_PROTECTION } from 'app/constants';

interface Props {
  onValidation: () => void;
  onCancel?: () => void;
}

const PinValidationModal: React.FC<Props> = ({ onValidation, onCancel }) => {
  const [validatePin, { isLoading }] = useValidatePinMutation();
  const [attempts, setAttempts] = useState(0);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutTimeRemaining, setLockoutTimeRemaining] = useState(0);

  // Validation schema for PIN
  const validationSchema = Yup.object({
    pin: Yup.string()
      .required('Le code PIN est requis')
      .matches(/^\d{4,6}$/, 'Le code PIN doit contenir entre 4 et 6 chiffres'),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      pin: '',
    },
    validationSchema,
    onSubmit: async values => {
      if (isLockedOut) {
        toast.error('Trop de tentatives échouées. Veuillez attendre.');
        return;
      }

      try {
        const result = await validatePin({ pin: values.pin }).unwrap();

        if (result.valid) {
          toast.success('Code PIN validé avec succès');
          setAttempts(0);
          onValidation();
        } else {
          const newAttempts = attempts + 1;
          setAttempts(newAttempts);

          if (newAttempts >= PIN_PROTECTION.MAX_ATTEMPTS) {
            setIsLockedOut(true);
            setLockoutTimeRemaining(
              PIN_PROTECTION.LOCKOUT_DURATION_MINUTES * 60,
            );
            toast.error(
              `Trop de tentatives échouées. Accès bloqué pendant ${PIN_PROTECTION.LOCKOUT_DURATION_MINUTES} minutes.`,
            );
          } else {
            const remainingAttempts = PIN_PROTECTION.MAX_ATTEMPTS - newAttempts;
            toast.error(
              `Code PIN incorrect. ${remainingAttempts} tentative(s) restante(s).`,
            );
          }

          formik.setFieldValue('pin', '');
        }
      } catch (error) {
        console.error('PIN validation error:', error);
        toast.error('Erreur lors de la validation du code PIN');
      }
    },
  });

  // Lockout timer effect
  useEffect(() => {
    let interval: number;

    if (isLockedOut && lockoutTimeRemaining > 0) {
      interval = setInterval(() => {
        setLockoutTimeRemaining(prev => {
          if (prev <= 1) {
            setIsLockedOut(false);
            setAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLockedOut, lockoutTimeRemaining]);

  const formatLockoutTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // Default behavior: redirect to home
      window.location.href = '/';
    }
  };

  return (
    <Modal
      open={true}
      onClose={() => {}}
      center={true}
      showCloseIcon={false}
      classNames={{
        overlay: 'bg-black bg-opacity-75',
        modal: 'rounded-lg shadow-xl max-w-md max-w-md',
      }}
    >
      <Card className="border-0 shadow-none">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Lock className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Accès sécurisé requis
          </CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            Veuillez saisir votre code PIN pour accéder au suivi de flotte
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="pin"
                className="text-sm font-medium text-gray-700"
              >
                Code PIN
              </Label>
              <Input
                id="pin"
                type="password"
                placeholder="Entrez votre code PIN"
                className={`text-center text-lg tracking-widest ${
                  formik.touched.pin && formik.errors.pin
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                disabled={isLockedOut || isLoading}
                autoComplete="off"
                maxLength={6}
                {...formik.getFieldProps('pin')}
              />
              {formik.touched.pin && formik.errors.pin && (
                <div className="flex items-center text-sm text-red-600 mt-1">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {formik.errors.pin}
                </div>
              )}
            </div>

            {isLockedOut && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium">Accès temporairement bloqué</p>
                    <p>
                      Temps restant: {formatLockoutTime(lockoutTimeRemaining)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {attempts > 0 && !isLockedOut && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                  <div className="text-sm text-yellow-700">
                    {PIN_PROTECTION.MAX_ATTEMPTS - attempts} tentative(s)
                    restante(s)
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="submit"
                disabled={isLockedOut || isLoading || !formik.values.pin}
                className="flex-1"
              >
                {isLoading ? 'Validation...' : 'Valider'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1"
              >
                Annuler
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Si vous avez oublié votre code PIN, contactez votre administrateur
            </p>
          </div>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default PinValidationModal;
